import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Trophy, Sparkles, ChevronRight, GraduationCap, TrendingUp } from 'lucide-react';
import { apiClient } from '@/lib/api';
import EnrolledLeadsModal from '@/components/EnrolledLeadsModal';
import logger from '@/lib/logger';

const fmtNum = (n) => (n ?? 0).toLocaleString();
const fmtAED = (n) => `AED ${Math.round(n || 0).toLocaleString()}`;
const fmtDate = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
        return iso.slice(0, 10);
    }
};

/**
 * Celebration card for the Sales Dashboard.
 * - All-time enrolled (hero number)
 * - This-month enrolled (secondary, with revenue)
 * - Latest enrollment ribbon ("🎉 abdul samad · Fathima VN · 2 May")
 * - Click anywhere → opens the existing EnrolledLeadsModal in all-time mode
 *
 * Distinct emerald gradient + soft confetti sparkle so it stands out from the
 * monochrome KPI strip below. Polls once per page load (the modal pulls fresh
 * data on open).
 */
export const TotalEnrolledCelebrationCard = ({ scope } = {}) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);

    const fetchSummary = useCallback(async () => {
        try {
            setLoading(true);
            const res = await apiClient.get('/dashboard/enrolled-summary', scope === 'floor' ? { params: { scope: 'floor' } } : {});
            setData(res.data);
        } catch (e) {
            logger.error('Failed to load enrolled summary', e);
        } finally {
            setLoading(false);
        }
    }, [scope]);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    const allTime = data?.all_time ?? 0;
    const thisMonth = data?.this_month ?? 0;
    const monthRevenue = data?.this_month_revenue ?? 0;
    const allTimeRevenue = data?.all_time_revenue ?? 0;
    const latest = data?.latest;

    return (
        <>
            <Card
                onClick={() => setModalOpen(true)}
                className="relative overflow-hidden cursor-pointer group border-emerald-500/40 hover:border-emerald-400 transition-all hover:shadow-[0_0_40px_-10px_rgba(16,185,129,0.45)]"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setModalOpen(true); } }}
                data-testid="total-enrolled-card"
            >
                {/* Gradient backdrop — emerald → teal, a warm achievement palette */}
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/15 via-teal-500/10 to-emerald-600/15 pointer-events-none" />

                {/* Soft confetti dots — purely decorative */}
                <div className="absolute top-3 right-6 h-1.5 w-1.5 rounded-full bg-amber-400/80 animate-pulse" />
                <div className="absolute top-8 right-16 h-1 w-1 rounded-full bg-emerald-300/80 animate-pulse" style={{ animationDelay: '0.3s' }} />
                <div className="absolute top-5 right-28 h-1 w-1 rounded-full bg-teal-300/80 animate-pulse" style={{ animationDelay: '0.6s' }} />
                <Sparkles className="absolute top-4 right-4 h-5 w-5 text-emerald-400/60 group-hover:text-emerald-300 transition-colors" />

                <div className="relative p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-6">
                    {/* Trophy badge */}
                    <div className="flex-shrink-0">
                        <div className="h-16 w-16 md:h-20 md:w-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:scale-105 transition-transform">
                            <Trophy className="h-8 w-8 md:h-10 md:w-10 text-white" strokeWidth={2.2} />
                        </div>
                    </div>

                    {/* Counts */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-300 text-xs uppercase tracking-widest font-semibold">
                            <GraduationCap className="h-4 w-4" />
                            Total Enrolled Students
                        </div>
                        <div className="mt-1 flex items-baseline gap-3 flex-wrap">
                            <span
                                className="text-4xl md:text-5xl font-bold tabular-nums bg-gradient-to-br from-emerald-500 to-teal-600 bg-clip-text text-transparent"
                                data-testid="total-enrolled-alltime"
                            >
                                {loading ? '—' : fmtNum(allTime)}
                            </span>
                            <span className="text-sm text-muted-foreground">
                                all-time · <span className="font-medium text-foreground">{fmtAED(allTimeRevenue)}</span>
                            </span>
                        </div>

                        {/* Month strip */}
                        <div className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 text-sm">
                            <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
                            <span className="text-foreground font-semibold tabular-nums" data-testid="total-enrolled-month">
                                {loading ? '—' : fmtNum(thisMonth)}
                            </span>
                            <span className="text-muted-foreground">in {data?.month_label || 'this month'}</span>
                            <span className="text-emerald-600 dark:text-emerald-300 font-medium">· {fmtAED(monthRevenue)}</span>
                        </div>

                        {/* Latest ribbon */}
                        {latest && (
                            <div className="mt-3 text-xs text-muted-foreground truncate" data-testid="total-enrolled-latest">
                                🎉 Latest:&nbsp;
                                <span className="text-foreground font-medium">{latest.full_name}</span>
                                {latest.assigned_to_name && <> · by <span className="text-foreground/80">{latest.assigned_to_name}</span></>}
                                {latest.enrolled_at && <> · {fmtDate(latest.enrolled_at)}</>}
                            </div>
                        )}
                    </div>

                    {/* CTA chevron */}
                    <div className="hidden md:flex flex-col items-end gap-1 text-emerald-600 dark:text-emerald-300 group-hover:translate-x-0.5 transition-transform">
                        <ChevronRight className="h-6 w-6" />
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">View list</span>
                    </div>
                </div>
            </Card>

            <EnrolledLeadsModal
                scope={scope}
                open={modalOpen}
                onOpenChange={setModalOpen}
                defaultPeriod="all"
            />
        </>
    );
};

export default TotalEnrolledCelebrationCard;
